import { RefObject, useLayoutEffect, useState } from 'react';

export interface ElementSize {
  width: number;
  height: number;
}

// The element's size in CSS pixels, read once on mount and then again once a resize has
// settled for `settleMs`, so a window drag costs one re-render at the end, not one per
// frame. With `settleMs` 0 it follows the resize as it happens, at most once a frame (the
// browser reports a resize once per frame). Scrolling never changes it. Both are 0 until
// the element has a size.
export const useElementSize = (
  ref: RefObject<HTMLElement | null>,
  settleMs: number,
): ElementSize => {
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const readSize = () => {
      const { width, height } = element.getBoundingClientRect();
      setSize((last) =>
        last.width === width && last.height === height ? last : { width, height },
      );
    };
    readSize();
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    const readWhenSettled = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(readSize, settleMs);
    };
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', readWhenSettled);
      return () => {
        clearTimeout(settleTimer);
        window.removeEventListener('resize', readWhenSettled);
      };
    }
    const observer = new ResizeObserver(settleMs > 0 ? readWhenSettled : readSize);
    observer.observe(element);
    return () => {
      clearTimeout(settleTimer);
      observer.disconnect();
    };
  }, [ref, settleMs]);

  return size;
};
